/**
 * Axios / fetch errors → user-visible strings.
 * fxbazzar_frontend uses `error.response.data.message`; APIs also send `Message`, `error`,
 * nested `errors`, or JSON-as-string. Avoid showing Axios’s generic "Network Error" when a body exists.
 */

function pickStr(v) {
  if (v == null) return "";
  const s = String(v).trim();
  return s || "";
}

/**
 * Same order as fxbazzar_frontend `marketWatch.slice` / `positionReport.slice` `.catch`:
 * `error.response.data.message` → `request.message` → `error.message`.
 * Also checks `data.Message` when `message` is missing (.NET-style APIs).
 */
export function legacyAxiosToastMessage(error) {
  if (!error) return "";
  if (error.response && error.response.data != null) {
    const d = error.response.data;
    const m = pickStr(d.message) || pickStr(d.Message);
    if (m) return m;
  }
  if (error.response?.status === 401) return "Session expired. Please log in again.";
  if (error.request && pickStr(error.request.message)) return pickStr(error.request.message);
  if (pickStr(error.message)) return pickStr(error.message);
  return "";
}

/**
 * Extract a human-readable message from an API error body (any shape).
 */
export function messageFromApiResponseData(data) {
  if (data == null || data === "") return "";
  if (typeof data === "string") {
    const s = data.trim();
    if (!s) return "";
    if (s.startsWith("{") || s.startsWith("[")) {
      try {
        return messageFromApiResponseData(JSON.parse(s));
      } catch {
        /* fall through */
      }
    }
    if (s.startsWith("<!") || s.startsWith("<html")) return "";
    return s.length > 320 ? `${s.slice(0, 320)}…` : s;
  }
  if (typeof data !== "object") return pickStr(data);

  let out =
    pickStr(data.message) ||
    pickStr(data.Message) ||
    pickStr(data.errorMessage) ||
    pickStr(data.error_description) ||
    pickStr(data.title) ||
    pickStr(data.detail) ||
    pickStr(data.Description);
  if (out) return out;

  if (typeof data.error === "string" && pickStr(data.error)) return pickStr(data.error);
  if (data.error && typeof data.error === "object") {
    const inner = messageFromApiResponseData(data.error);
    if (inner) return inner;
  }

  if (Array.isArray(data.errors)) {
    const first = data.errors[0];
    if (typeof first === "string" && pickStr(first)) return pickStr(first);
    if (first && typeof first === "object") {
      const e = pickStr(first.message) || pickStr(first.Message) || messageFromApiResponseData(first);
      if (e) return e;
    }
  }

  if (data.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
    for (const v of Object.values(data.errors)) {
      if (Array.isArray(v) && v.length) {
        const x = pickStr(v[0]);
        if (x) return x;
      } else if (typeof v === "string" && pickStr(v)) {
        return pickStr(v);
      }
    }
  }

  if (data.data != null && typeof data.data === "object") {
    const nested = messageFromApiResponseData(data.data);
    if (nested) return nested;
  }

  return "";
}

/**
 * Legacy-style: `error.response` → body message; else request; else `error.message`.
 * Prefer API text over the literal "Network Error" when possible.
 */
export function getAxiosErrorMessage(error, fallback = "Request failed") {
  if (!error) return fallback;

  const res = error.response;
  if (res && res.data !== undefined && res.data !== null) {
    const fromBody = messageFromApiResponseData(res.data);
    if (fromBody) return fromBody;
  }

  if (res) {
    const st = res.status;
    const txt = pickStr(res.statusText);
    if (st && txt) return `Error ${st}: ${txt}`;
    if (st) return `Request failed (${st})`;
  }

  const reqMsg = error.request && error.request.message != null ? pickStr(error.request.message) : "";
  if (reqMsg) return reqMsg;

  const em = error.message != null ? pickStr(error.message) : "";
  if (em && em !== "Network Error") return em;

  return fallback;
}

/** Value passed to rejectWithValue in async thunks. */
export function axiosRejectPayload(error) {
  const from = error?.response?.data != null ? messageFromApiResponseData(error.response.data) : "";
  if (from) return from;
  if (error?.response?.status === 401) return "Session expired. Please log in again.";
  if (error?.message && pickStr(error.message) !== "Network Error") return pickStr(error.message);
  return "Request failed";
}
